import React from 'react'
import classNames from 'classnames'
import { Spinner } from './Spinner'


interface FillImageViewProps {
  imgRef?: React.MutableRefObject<HTMLImageElement | null>
  src: string
  placeholder?: string
  className?: string
  style?: React.CSSProperties
  loadingIndicator?: boolean
}

export enum FillImageViewState {
  Loading = 'loading',
  Loaded = 'loaded',
  Error = 'error',
}

interface StatelessImageViewProps extends FillImageViewProps {
  setState: (state: FillImageViewState) => void
  state: FillImageViewState
}

export function StatelessFillImageView(props: StatelessImageViewProps) {
  const { placeholder, className, loadingIndicator, style, state, setState, imgRef } = props

  // const [state, setState] = React.useState(FillImageViewState.Loading)
  const [widthToHeightRatio, setWidthToHeightRatio] = React.useState(1)

  const ref = React.useRef<HTMLImageElement | null>(null)

  React.useLayoutEffect(() => {
    if (ref.current) {
      if (ref.current.src === placeholder) {
        setState(FillImageViewState.Error)
      } else if (ref.current.complete) {
        setState(FillImageViewState.Loaded)
      } else {
        setState(FillImageViewState.Loading)
      }
    }
  }, [placeholder, props.src, setState])

  React.useLayoutEffect(() => {
    if (state === FillImageViewState.Loaded && ref.current) {
      setWidthToHeightRatio(ref.current.naturalWidth / ref.current.naturalHeight)
    }
  }, [state])

  return (
    <>
      <img
        src={props.src}
        ref={img => {
          ref.current = img
          if (imgRef) {
            imgRef.current = img
          }
        }}
        onLoad={e => setState((e.target as HTMLImageElement).src === placeholder ? FillImageViewState.Error : FillImageViewState.Loaded)}
        onError={() => setState(FillImageViewState.Error)}
        alt=""
        className={classNames('FillImageView', className, state)}
        style={{
          ...style,
          opacity: state === FillImageViewState.Loaded ? 1 : 0,
          width: widthToHeightRatio < 1 ? 'auto' : '100%',
          height: widthToHeightRatio >= 1 ? 'auto' : '100%',
          left: `${(100 - (100 * Math.min(widthToHeightRatio, 1))) / 2}%`,
          top: `${(100 - (100 / Math.max(widthToHeightRatio, 1))) / 2}%`,
          position: 'absolute',
        }}
      />
      {
        (placeholder && state === FillImageViewState.Error) && (
          <img
            src={placeholder}
            alt=""
            className={classNames('FillImageView', className, state)}
            style={{
              ...style,
              width: '100%',
              height: '100%',
              position: 'absolute',
            }}
          />
        )
      }
      {
        (loadingIndicator && state === FillImageViewState.Loading) && (
          <Spinner center />
        )
      }
    </>
  )
}

function FillImageViewUnMemoed(props: FillImageViewProps) {
  const [state, setState] = React.useState(FillImageViewState.Loading)

  return (
    <StatelessFillImageView
      {...props}
      state={state}
      setState={setState}
    />
  )
}

const FillImageView = React.memo<FillImageViewProps>(
  FillImageViewUnMemoed
)

export default FillImageView
